import React, {ReactNode, useEffect, useRef} from 'react';
import {noop} from './Utils';

export interface FocusBoundaryProps {
  readonly children: ReactNode;
}

/**
 * Traps the focus within the boundary element.
 */
function FocusTrap({ children }: FocusBoundaryProps) {

  const boundaryRef = useRef<HTMLDivElement | null>(null);

  const [previousActiveElement, setPreviousActiveElement] = React.useState<Element | null>(null);
  const previousActiveElementRef = useRef(previousActiveElement);

  // Keep references in sync with state

  useEffect(() => {
    previousActiveElementRef.current = previousActiveElement;
  }, [previousActiveElement]);

  // Initialise component

  useEffect((): () => void => {
    setPreviousActiveElement(document.activeElement);

    const boundary: HTMLElement | null = boundaryRef.current;
    if (!boundary) {
      return noop;
    }

    const focusableSelectors: string[] = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex=\'-1\'])'
    ];

    const getFocusable = (): HTMLElement[] => {
      const elements: NodeListOf<HTMLElement> = boundary.querySelectorAll<HTMLElement>(
          focusableSelectors.join(',')
      );
      return Array.from(elements);
    };

    const handleFocusIn = (event: FocusEvent): void => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      if (!boundary.contains(target)) {
        const focusable: HTMLElement[] = getFocusable();
        if (focusable.length === 0) {
          return;
        }

        // Redirect focus to the first element
        focusable[0].focus();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusable: HTMLElement[] = getFocusable();
      if (focusable.length === 0) {
        return;
      }

      const first: HTMLElement = focusable[0];
      const last: HTMLElement = focusable[focusable.length - 1];
      const active: Element | null = document.activeElement;

      const activeIsHTMLElement =
                active instanceof HTMLElement ? active : null;

      // Shift+Tab on first -> wrap to last
      if (event.shiftKey && activeIsHTMLElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      // Tab on last -> wrap to first
      if (!event.shiftKey && activeIsHTMLElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKeyDown);

      if (previousActiveElementRef.current) {
        (previousActiveElementRef.current as any).focus();
      }
    };
  }, []);

  // Render component

  return <div ref={boundaryRef}>
    {children}
  </div>;
}

export default FocusTrap;
